import type { EnrichedQuote } from '../calculations/roi/enrichQuotesWithROI';
import { getQuotePaymentOption, getQuotePayYears } from '../guidedPlanningPreferences';

type QuoteEnrichment = Pick<
  EnrichedQuote,
  'projectedCostCoverage' | 'roi' | 'formattedROI' | 'maxAchievableCoverage'
>;

export type HybridConsumerQuote = EnrichedQuote & {
  /** Set when the quote was re-priced to a tier's premium: the premium the carrier quoted it at. */
  repricedFromPremium?: number;
};

export type ConsumerQuote = HybridConsumerQuote | (TraditionalLTCQuote & QuoteEnrichment);

export const CONSUMER_PAYMENT_SCHEDULES: readonly ConsumerPaymentSchedule[] = [
  'ongoing',
  'tenPay',
  'singlePay',
];

export const DEFAULT_CONSUMER_PAYMENT_SCHEDULE: ConsumerPaymentSchedule = 'tenPay';

const TEN_PAY_YEARS = 10;

export function isTraditionalConsumerQuote(
  quote: ConsumerQuote,
): quote is TraditionalLTCQuote & QuoteEnrichment {
  return 'traditionalLTCQuoteId' in quote;
}

/** A re-priced quote's key carries its premium, so one carrier quote can be offered at two premiums. */
export function consumerQuoteKey(quote: ConsumerQuote): string {
  if (isTraditionalConsumerQuote(quote)) return `traditional-${quote.traditionalLTCQuoteId}`;
  const key = `hybrid-${quote.insuranceQuoteId}`;
  return quote.repricedFromPremium === undefined ? key : `${key}@${quote.premiumAmount}`;
}

const REPRICED_QUOTE_KEY = /^hybrid-(.+)@(\d+(?:\.\d+)?)$/;

export function parseRepricedQuoteKey(key: string): { insuranceQuoteId: string; premium: number } | null {
  const match = REPRICED_QUOTE_KEY.exec(key);
  return match ? { insuranceQuoteId: match[1], premium: Number(match[2]) } : null;
}

export function consumerQuoteCarrier(quote: ConsumerQuote): string {
  return isTraditionalConsumerQuote(quote) ? quote.carrier : quote.carrierName;
}

export function paymentScheduleOfQuote(quote: ConsumerQuote): ConsumerPaymentSchedule | null {
  const paymentOption = getQuotePaymentOption(quote);
  if (paymentOption === 'Single Pay') return 'singlePay';
  if (paymentOption === 'Monthly') return 'ongoing';
  if (getQuotePayYears(quote) === TEN_PAY_YEARS) return 'tenPay';
  return null;
}

/** The amount shown to the consumer: monthly for ongoing, annual for 10-pay, the lump sum for single pay. */
export function consumerQuotePremium(quote: ConsumerQuote): number {
  if (isTraditionalConsumerQuote(quote)) return quote.primaryModalPremium;
  return quote.premiumAmount;
}

/** Total premiums paid, assuming an ongoing premium is paid unchanged until care starts. */
export function consumerQuoteTotalPremiums(quote: ConsumerQuote, yearsUntilCare: number): number {
  const premium = consumerQuotePremium(quote);
  switch (paymentScheduleOfQuote(quote)) {
    case 'singlePay':
      return premium;
    case 'tenPay':
      return premium * TEN_PAY_YEARS;
    default:
      return premium * 12 * Math.max(0, yearsUntilCare);
  }
}

export function isHybridConsumerQuote(quote: ConsumerQuote): quote is HybridConsumerQuote {
  return !isTraditionalConsumerQuote(quote);
}

export function quotesForSchedule(
  quotes: ConsumerQuote[],
  schedule: ConsumerPaymentSchedule,
): ConsumerQuote[] {
  return quotes.filter(quote => paymentScheduleOfQuote(quote) === schedule);
}

export type ConsumerOptionLabel = 'lowerPremium' | 'bestValue' | 'moreCoverage';

export interface ConsumerCoverageOption {
  quote: ConsumerQuote;
  label: ConsumerOptionLabel;
}

/**
 * Re-prices a carrier quote toward another premium and prices it against the consumer's care plan.
 * The result is at the nearest premium the carrier can issue, which may not be the one asked for;
 * null when the quote cannot move off its stored premium.
 */
export type RepriceConsumerQuote = (quote: ConsumerQuote, premium: number) => ConsumerQuote | null;

/** Where a quote re-priced toward a premium would land, and the highest ROI it could reach there. */
export interface RepricedQuoteEstimate {
  premium: number;
  roiCeiling: number;
}

/**
 * `estimate` is cheap and never runs the care-plan projection; `reprice` does, and is what costs
 * time. The picker only reprices quotes whose ceiling could still beat the best one found.
 */
export interface ConsumerQuoteRepricer {
  estimate: (quote: ConsumerQuote, premium: number) => RepricedQuoteEstimate | null;
  reprice: RepriceConsumerQuote;
}

// Quotes with a premium from `min` up to, but not including, `max`: stored quotes already there, plus
// every other quote re-priced toward the `targets` premiums that lands there. Within a step the
// option is the best coverage per premium dollar unless `pick` asks for the cheapest.
interface PremiumStep {
  min?: number;
  max?: number;
  targets?: number[];
  pick?: 'bestRoi' | 'lowestPremium';
}

/**
 * Where each option looks for its quote, by payment schedule, as steps tried in order. This is the
 * product spec for the three cards:
 *
 * - Ongoing: under $100/mo, else the cheapest $100–$200 / best ROI $100–$200 / best ROI over $200.
 * - 10-pay: best ROI under $10k a year, ideally around $7k / the $10k range / best ROI over $11k,
 *   ideally around $12k.
 * - Single pay: $50k if possible, else $75k–$95k / the $100k range / the $150k range, else the
 *   closest to $150k a carrier allows above the $100k range.
 *
 * Amounts are the premium as each schedule is priced: per month for ongoing, per year for 10-pay,
 * and the lump sum for single pay. Hybrid carriers are quoted at one premium per product ($10,000 a
 * year 10-pay, $100,000 single pay), so the other tiers are only reachable by re-pricing toward
 * `targets`. Traditional quotes are never re-priced.
 */
const COVERAGE_OPTION_STEPS: Record<ConsumerPaymentSchedule, Record<ConsumerOptionLabel, PremiumStep[]>> = {
  ongoing: {
    lowerPremium: [{ max: 100 }, { min: 100, max: 200, pick: 'lowestPremium' }],
    bestValue: [{ min: 100, max: 200 }],
    moreCoverage: [{ min: 200 }],
  },
  tenPay: {
    lowerPremium: [{ min: 6000, max: 8000, targets: [7000] }, { max: 10_000, targets: [7000] }],
    bestValue: [{ min: 10_000, max: 11_000, targets: [10_000] }],
    moreCoverage: [{ min: 11_500, max: 12_500, targets: [12_000] }, { min: 11_000, targets: [12_000] }],
  },
  singlePay: {
    lowerPremium: [
      { min: 45_000, max: 55_000, targets: [50_000] },
      { min: 75_000, max: 95_000, targets: [75_000, 85_000] },
    ],
    bestValue: [{ min: 95_000, max: 115_000, targets: [100_000] }],
    // When no carrier can be issued in the $150k range, the closest premium a carrier allows above the
    // $100k range, which is the most coverage on offer (product decision, 20 Oct 2026).
    moreCoverage: [{ min: 135_000, max: 165_000, targets: [150_000] }, { min: 115_000, targets: [150_000] }],
  },
};

const OPTION_ORDER: ConsumerOptionLabel[] = ['lowerPremium', 'bestValue', 'moreCoverage'];

// Where an option with nothing in its own range borrows from: the nearest range first, and for the
// middle option the higher range before the lower one.
const BORROW_ORDER: Record<ConsumerOptionLabel, ConsumerOptionLabel[]> = {
  lowerPremium: ['bestValue', 'moreCoverage'],
  bestValue: ['moreCoverage', 'lowerPremium'],
  moreCoverage: ['bestValue', 'lowerPremium'],
};

function repricedQuoteKey(quote: ConsumerQuote, premium: number): string {
  return consumerQuoteKey({ ...quote, premiumAmount: premium, repricedFromPremium: consumerQuotePremium(quote) } as ConsumerQuote);
}

function rankBy(quotes: ConsumerQuote[], pick: PremiumStep['pick'] = 'bestRoi'): ConsumerQuote | undefined {
  const ranked =
    pick === 'lowestPremium'
      ? quotes.sort((a, b) => consumerQuotePremium(a) - consumerQuotePremium(b))
      : quotes.sort(
          (a, b) => (b.roi ?? 0) - (a.roi ?? 0) || consumerQuotePremium(a) - consumerQuotePremium(b),
        );
  return ranked[0];
}

/**
 * Narrows a payment schedule's quotes to up to three options, one per premium tier, each the
 * first step in its tier with a quote. With `repricer`, a step also offers every quote re-priced
 * toward its target premiums that lands in its range; without it only stored premiums are
 * considered. A tier with nothing in its own steps takes the next best value quote from the nearest
 * tier's steps, so a schedule priced at a single premium still offers three options. No quote is
 * offered twice at the same premium. The options read cheapest first; quotes at the same premium
 * keep the tier order, so the middle option stays its own range's best value. When no tier
 * matches, the best value quote is offered alone so the schedule never disappears.
 */
export function pickCoverageOptions(
  quotes: ConsumerQuote[],
  schedule: ConsumerPaymentSchedule,
  repricer?: ConsumerQuoteRepricer,
): ConsumerCoverageOption[] {
  const repriced = new Map<string, ConsumerQuote | null>();
  const repricedAt = (quote: ConsumerQuote, premium: number) => {
    const key = `${consumerQuoteKey(quote)}@${premium}`;
    if (!repriced.has(key)) repriced.set(key, repricer?.reprice(quote, premium) ?? null);
    return repriced.get(key) ?? null;
  };

  const picks = new Map<ConsumerOptionLabel, ConsumerQuote>();
  // Re-priced quotes are priced against the care plan in order of their ROI ceiling, stopping once
  // the best quote found beats the next ceiling, so the pick matches pricing every quote at a
  // fraction of the cost (see repriceConsumerQuote for how the ceiling is set). A tie with the
  // ceiling is still priced, since it could win on premium.
  const bestInStep = ({ min = 0, max = Infinity, targets = [], pick = 'bestRoi' }: PremiumStep) => {
    const isInRange = (premium: number) => premium >= min && premium < max;
    const taken = new Set([...picks.values()].map(consumerQuoteKey));
    const stored = quotes.filter(
      quote => isInRange(consumerQuotePremium(quote)) && !taken.has(consumerQuoteKey(quote)),
    );
    if (!repricer || !targets.length) return rankBy(stored, pick);

    const scalable = quotes
      .filter(quote => isHybridConsumerQuote(quote) && !isInRange(consumerQuotePremium(quote)))
      .flatMap(quote => targets.map(target => ({ quote, target, estimate: repricer.estimate(quote, target) })))
      .flatMap(({ quote, target, estimate }) =>
        estimate && isInRange(estimate.premium) && !taken.has(repricedQuoteKey(quote, estimate.premium))
          ? [{ quote, target, roiCeiling: estimate.roiCeiling }]
          : [],
      );
    const priced = (candidate: (typeof scalable)[number]) => {
      const quote = repricedAt(candidate.quote, candidate.target);
      return quote && isInRange(consumerQuotePremium(quote)) && !taken.has(consumerQuoteKey(quote)) ? quote : null;
    };
    if (pick === 'lowestPremium') {
      return rankBy([...stored, ...scalable.map(priced).filter((quote): quote is ConsumerQuote => !!quote)], pick);
    }

    let best = rankBy(stored);
    for (const candidate of scalable.sort((a, b) => b.roiCeiling - a.roiCeiling)) {
      if (best && (best.roi ?? 0) > candidate.roiCeiling) break;
      const quote = priced(candidate);
      if (quote && (!best || rankBy([best, quote]) === quote)) best = quote;
    }
    return best;
  };
  // Steps are tried one at a time, since a later step can re-price every quote again.
  const firstInRange = (label: ConsumerOptionLabel, pick?: PremiumStep['pick']) => {
    for (const step of COVERAGE_OPTION_STEPS[schedule][label]) {
      const quote = bestInStep({ ...step, pick: pick ?? step.pick });
      if (quote) return quote;
    }
    return undefined;
  };

  for (const label of OPTION_ORDER) {
    const quote = firstInRange(label);
    if (quote) picks.set(label, quote);
  }

  if (!picks.size) {
    const bestValue = rankBy([...quotes]);
    return bestValue ? [{ quote: bestValue, label: 'bestValue' }] : [];
  }

  for (const label of OPTION_ORDER) {
    if (picks.has(label)) continue;
    for (const source of BORROW_ORDER[label]) {
      const quote = firstInRange(source, 'bestRoi');
      if (!quote) continue;
      picks.set(label, quote);
      break;
    }
  }

  const inTierOrder = OPTION_ORDER.flatMap(label => {
    const quote = picks.get(label);
    return quote ? [{ quote, label }] : [];
  });
  return inTierOrder
    .map(option => option.quote)
    .sort((a, b) => consumerQuotePremium(a) - consumerQuotePremium(b))
    .map((quote, index) => ({ quote, label: inTierOrder[index].label }));
}

export type ConsumerCoverageOptions = Record<ConsumerPaymentSchedule, ConsumerCoverageOption[]>;

export function coverageOptionsBySchedule(
  quotes: ConsumerQuote[],
  repricer?: ConsumerQuoteRepricer,
): ConsumerCoverageOptions {
  return Object.fromEntries(
    CONSUMER_PAYMENT_SCHEDULES.map(schedule => [
      schedule,
      pickCoverageOptions(quotesForSchedule(quotes, schedule), schedule, repricer),
    ]),
  ) as ConsumerCoverageOptions;
}

/** A saved re-priced choice, priced again at the premium it was chosen at. */
export function repricedQuoteFromKey(
  key: string,
  quotes: ConsumerQuote[],
  repriceQuote: RepriceConsumerQuote,
): ConsumerQuote | null {
  const parts = parseRepricedQuoteKey(key);
  const stored = parts && quotes.find(quote => consumerQuoteKey(quote) === `hybrid-${parts.insuranceQuoteId}`);
  return stored ? repriceQuote(stored, parts.premium) : null;
}

/** Every quote a key can point at: the stored quotes, the re-priced options and a re-priced saved choice. */
export function quotesWithOffered(
  quotes: ConsumerQuote[],
  coverageOptions: ConsumerCoverageOptions,
  chosenQuote?: ConsumerQuote | null,
): ConsumerQuote[] {
  const offered = [...Object.values(coverageOptions).flat().map(option => option.quote), ...(chosenQuote ? [chosenQuote] : [])];
  const byKey = new Map(quotes.map(quote => [consumerQuoteKey(quote), quote]));
  for (const quote of offered) {
    if (!byKey.has(consumerQuoteKey(quote))) byKey.set(consumerQuoteKey(quote), quote);
  }
  return [...byKey.values()];
}

export function countCarriers(quotes: ConsumerQuote[]): number {
  return new Set(quotes.map(consumerQuoteCarrier)).size;
}
